import { useFloatingScrollbar } from "@tellescope/react-hooks";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { addMinutes } from "date-fns/addMinutes";
import { startOfDay } from "date-fns/startOfDay";
import { useCallback, useMemo, useRef, useState } from "react";
import { DAY_COLUMN, getDaysOfWeek, LOOKBACK_IN_MS_BY_VIEW, MONDAY_START_DAYS_OF_WEEK } from "../constants";
import { getEventDayLayouts, getEventsByDate, getHiddenEventsFlags } from "../../utils";
import { EventsByDateWithLayout } from "../../types";
import { useCalendarEventsWithFilters, useDayBoundsMinutes } from "../../hooks";
import { FAKE_CALENDAR_USER_ID } from "../../filters";
import type { CalendarWeekViewProps } from "./CalendarWeekView";

export const useCalendarWeekViewLogic = ({
  referenceDate,
  weekStart,
  timezone,
  filters,
  currentUserId,
}: CalendarWeekViewProps) => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const showTwoTimezones = browserTimezone !== timezone;
  const daysOfWeek = weekStart === "monday" ? MONDAY_START_DAYS_OF_WEEK : getDaysOfWeek();
  const [creationMetadata, setCreationMetadata] = useState<{
    isCreating: boolean;
    initialTimestamp: number;
    initialDurationMinutes?: number;
  }>();
  const { dayStartMinutes: baseDayStartMinutes, dayEndMinutes: baseDayEndMinutes } = useDayBoundsMinutes();
  const [expandEarlier, setExpandEarlier] = useState(false);
  const [expandLater, setExpandLater] = useState(false);

  const [prevReferenceTime, setPrevReferenceTime] = useState(referenceDate.getTime());

  const dayStartMinutes = expandEarlier ? 0 : baseDayStartMinutes;
  const dayEndMinutes = expandLater ? 24 * 60 : baseDayEndMinutes;

  const handleCreateClick = useCallback((initialTimestamp: number, initialDurationMinutes?: number) => {
    setCreationMetadata({ isCreating: true, initialTimestamp, initialDurationMinutes });
  }, []);

  const selectedUserIds = useMemo(
    () => (filters?.userIds ?? []).filter((id) => id !== FAKE_CALENDAR_USER_ID),
    [filters?.userIds]
  );

  const closeCreation = useCallback(() => setCreationMetadata(undefined), []);

  const eventsQuery = useCalendarEventsWithFilters({
    referenceDate,
    view: "week",
    timezone,
    weekStart,
    lookbackInMS: LOOKBACK_IN_MS_BY_VIEW.week,
    filters,
    currentUserId,
  });

  const { multiDayEvents, multiDayEventsByDate, normalEventsByDate } = useMemo(() => {
    const split = (eventsQuery.data ?? []).reduce<{
      multiDayEvents: TellescopeModelFromName<"calendar_events">[];
      normalEvents: TellescopeModelFromName<"calendar_events">[];
    }>(
      (acc, curr) => {
        const eventStartDate = new Date(curr.startTimeInMS);
        const eventEndDate = addMinutes(eventStartDate, curr.actualDuration ?? curr.durationInMinutes);
        const isMultiDay = startOfDay(eventStartDate).getTime() !== startOfDay(eventEndDate).getTime();
        if (isMultiDay) {
          acc.multiDayEvents.push(curr);
        } else {
          acc.normalEvents.push(curr);
        }
        return acc;
      },
      { multiDayEvents: [], normalEvents: [] }
    );

    const groupedNormalEvents = getEventsByDate(split.normalEvents, timezone);
    const normalEventsByDate: EventsByDateWithLayout = {};
    for (const timestampKey of Object.keys(groupedNormalEvents)) {
      const timestamp = Number(timestampKey);
      const dayEvents = groupedNormalEvents[timestamp] ?? [];
      const layouts = getEventDayLayouts(dayEvents);
      normalEventsByDate[timestamp] = dayEvents.map((event) => ({
        ...event,
        layout: layouts.get(event.id) ?? { widthPercent: 100, leftPercent: 0 },
      }));
    }

    return {
      multiDayEvents: split.multiDayEvents,
      multiDayEventsByDate: getEventsByDate(split.multiDayEvents, timezone),
      normalEventsByDate,
    };
  }, [eventsQuery.data, timezone]);

  const headersRef = useRef<HTMLDivElement>(null);
  const {
    scrollContainerRef,
    thumbRef,
    handleScroll: _handleScroll,
  } = useFloatingScrollbar({ trackInsetTopRef: headersRef });

  const [isNearTop, setIsNearTop] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(false);

  if (referenceDate.getTime() !== prevReferenceTime) {
    setPrevReferenceTime(referenceDate.getTime());
    setExpandEarlier(false);
    setExpandLater(false);
    setIsNearTop(false);
    setIsNearBottom(false);
  }
  const handleScroll = useCallback(() => {
    _handleScroll();
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    setIsNearTop(scrollTop <= 40);
    setIsNearBottom(scrollHeight - scrollTop - clientHeight <= 40);
  }, [_handleScroll, scrollContainerRef]);

  const handleExpandEarlier = useCallback(() => {
    const heightAdded = baseDayStartMinutes * DAY_COLUMN.MINUTE_HEIGHT;
    setExpandEarlier(true);
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTop = heightAdded;
      requestAnimationFrame(() => el.scrollTo({ top: 0, behavior: "smooth" }));
    });
  }, [baseDayStartMinutes, scrollContainerRef]);

  const handleExpandLater = useCallback(() => {
    setExpandLater(true);
    requestAnimationFrame(() => {
      const el = scrollContainerRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, [scrollContainerRef]);

  const { hasEarlierHiddenEvents, hasLaterHiddenEvents } = useMemo(
    () => getHiddenEventsFlags(normalEventsByDate, dayStartMinutes, dayEndMinutes),
    [normalEventsByDate, dayStartMinutes, dayEndMinutes]
  );

  return {
    browserTimezone,
    showTwoTimezones,
    daysOfWeek,
    multiDayEvents,
    multiDayEventsByDate,
    normalEventsByDate,
    scrollContainerRef,
    thumbRef,
    handleScroll,
    headersRef,
    creationMetadata,
    handleCreateClick,
    selectedUserIds,
    closeCreation,
    dayStartMinutes,
    dayEndMinutes,
    hasEarlierHiddenEvents,
    hasLaterHiddenEvents,
    handleExpandEarlier,
    handleExpandLater,
    isNearTop,
    isNearBottom,
  };
};
